import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { Tooltip, ListItemIcon } from "@mui/material";
import FeedOutlinedIcon from "@mui/icons-material/FeedOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import Storefront from "@mui/icons-material/Storefront";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { visuallyHidden } from "@mui/utils";
import { isEmpty, isNull } from "lodash";
import { useSelector, useDispatch } from "react-redux";
import Toggle from "../Toggle";
import { rowPerPageOptions } from "../../../components/CustomPagination";
import CustomPaginationActions from "../../../components/CustomPaginationActions";
import {
  setStoreDetails,
  setDataguideInProgress,
} from "../../../store/globalSlice";
import PermissionsGate from "../../../permissions/PermissionsGate";
import { SCOPES } from "../../../permissions/permissions-map";
import useRequest from "../../../hooks/useRequest";
import configData from "../../../config";
import { FILL_IN_ORDER_TYPE_ID, UPLOAD_STATUS } from "../../../utils";
import FileExport from "../../../components/FileExport";

const COMPLETED_STATUS_CODE = 4;

const numberColumns = ["MemberNumber", "ModelStoreNumber"];

function descendingComparator(a, b, orderBy) {
  let aVal = "";
  let bVal = "";
  if (orderBy === "CreatedDate") {
    aVal = new Date(a[orderBy]);
    bVal = new Date(b[orderBy]);
  } else {
    aVal =
      numberColumns.includes(orderBy) || typeof a[orderBy] === "number"
        ? Number(a[orderBy]) ?? ""
        : isEmpty(a[orderBy])
        ? ""
        : String(a[orderBy]).trim().toLowerCase();

    bVal =
      typeof b[orderBy] === "number"
        ? b[orderBy]
        : isEmpty(b[orderBy])
        ? ""
        : String(b[orderBy]).trim().toLowerCase();
  }

  if (bVal < aVal) {
    return -1;
  }

  if (bVal > aVal) {
    return 1;
  }

  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "MemberNumber",
    label: "Mbr",
  },
  {
    id: "StoreName",
    label: "Store Name",
  },
  {
    id: "ProjectType",
    label: "Project Type",
  },
  {
    id: "OrderType",
    label: "Order Type",
  },
  {
    id: "ModelStoreNumber",
    label: "Model Store",
  },
  {
    id: "AssignedMac",
    label: "Assigned MAC",
  },
  {
    id: "AssignedPlanner",
    label: "Project Planner",
  },
  {
    id: "ProjectStatus",
    label: "Status",
  },
  {
    id: "City",
    label: "City",
  },
  {
    id: "State",
    label: "State",
  },
  {
    id: "CreatedDate",
    label: "Created Date",
  },
  {
    id: "actions",
    label: "Actions",
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            padding="none"
            sx={{
              pl: 1,
              py: 1,
              fontWeight: "bold",
              backgroundColor: "#E7E7E7",
            }}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {!["actions"].includes(headCell.id) ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

const actionList = [
  {
    type: "details",
    label: "Details",
    icon: FeedOutlinedIcon,
    link: "project-details",
  },
  {
    type: "pogs",
    label: "Planograms",
    icon: TableChartOutlinedIcon,
    link: "manage-planograms/departments",
  },
  {
    type: "storeSupplies",
    label: "Store Supplies",
    icon: Storefront,
    link: "store-supplies/pogs",
  },
];

export default function DashboardTable(props) {
  const dispatch = useDispatch();
  const { requestMaker } = useRequest();
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("MemberNumber");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const store = useSelector((store) => store.global.store);
  const user = useSelector((store) => store.global.user);

  React.useEffect(() => {
    setPage(0);
  }, [props]);

  const handleActionMenuClk = (row) => (event) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setSelectedRow(null);
    setAnchorEl(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getDetails = (_event, row) => {
    dispatch(
      setStoreDetails({
        id: row.StoreId,
        memberNumber: !isNull(row.CheckDigit)
          ? `${row.MemberNumber}-${row.CheckDigit}`
          : row.MemberNumber,
        name: row.StoreName,
        creditLimit: row.CreditLimit,
        pending: row.IsPending,
        rdcNumber: row.RDCNumber,
        isArchived: row.IsArchived,
        isCompleted: row.StatusCode === COMPLETED_STATUS_CODE ? true : false,
        numberOfItemsPresent: row?.NumberOfItemsPresent,
        numberOfReleasedItemsPresent: row?.NumberOfReleasedItemsPresent,
        isFillInOrder: row?.OrderTypeId === FILL_IN_ORDER_TYPE_ID,
        orderTypeId: row?.OrderTypeId,
      })
    );
    navigate("project-details");
  };

  const handleOrderClick = React.useCallback(
    (row, link = "") => {
      requestMaker({
        method: "post",
        url: configData.GetDataguideDetails,
        data: { StoreId: store?.id, UserId: user?.email },
      })
        .then((res) => {
          const details = typeof res?.data === "object" ? res?.data : [];
          if (details.length > 0) {
            dispatch(
              setDataguideInProgress(
                details[0].Status === UPLOAD_STATUS[2] ? true : false
              )
            );
          } else {
            dispatch(setDataguideInProgress(false));
          }
        })
        .catch((err) => {
          if (err.response.status === 404) {
            dispatch(setDataguideInProgress(false));
          }
        });
      if (!!row?.StoreId) {
        dispatch(
          setStoreDetails({
            id: row.StoreId,
            memberNumber: !isNull(row.CheckDigit)
              ? `${row.MemberNumber}-${row.CheckDigit}`
              : row.MemberNumber,
            name: row.StoreName,
            creditLimit: row.CreditLimit,
            pending: row.IsPending,
            rdcNumber: row.RDCNumber,
            isArchived: row.IsArchived,
            isCompleted:
              row.StatusCode === COMPLETED_STATUS_CODE ? true : false,
            isFillInOrder: row?.OrderTypeId === FILL_IN_ORDER_TYPE_ID,
            orderTypeId: row?.OrderTypeId,
          })
        );
        navigate(link);
      }
    },
    [navigate, dispatch]
  );

  const handleActionSelect = (option) => (event) => {
    const { type, link } = option;
    if (type === "details") {
      getDetails(event, selectedRow);
    } else {
      handleOrderClick(selectedRow, link);
    }
    handleClose();
  };

  const menuRenderer = useMemo(() => {
    const list =
      selectedRow?.OrderTypeId === 6
        ? actionList.filter((_o) => _o.type !== "pogs")
        : actionList;
    return list.map((option) => (
      <MenuItem key={option.label} onClick={handleActionSelect(option)}>
        <ListItemIcon>
          <option.icon size="small" />
        </ListItemIcon>
        {option.label}
      </MenuItem>
    ));
  }, [handleActionSelect, selectedRow?.OrderTypeId]);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - props.data.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          p: 1,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          borderBottom: "1px solid grey",
        }}
      >
        <Grid container alignItems="center">
          <Grid item xs={6}>
            <Toggle />
          </Grid>
          <Grid
            item
            xs={6}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <PermissionsGate scopes={[SCOPES.canCreate]}>
              <Link to="create-project" className="link">
                <Button variant="contained">Add New Project</Button>
              </Link>
            </PermissionsGate>
          </Grid>
        </Grid>
        {!!props.data.length && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <FileExport type="dashboard" data={props.data} />
          </Box>
        )}
      </Paper>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer sx={{ maxHeight: 450 }}>
          <Table stickyHeader aria-label="sticky table">
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {stableSort(props.data, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  return (
                    <TableRow
                      hover
                      key={row.StoreId}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          pl: 1,
                          display: "table-cell",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 70,
                          color: (theme) => theme.palette.primary.main,
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        <Tooltip
                          title={
                            isNull(row.CheckDigit)
                              ? row.MemberNumber ?? ""
                              : `${row.MemberNumber}-${row.CheckDigit}`
                          }
                          arrow
                        >
                          <span onClick={(event) => getDetails(event, row)}>
                            {isNull(row.CheckDigit)
                              ? row.MemberNumber ?? ""
                              : `${row.MemberNumber}-${row.CheckDigit}`}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          pl: 1,
                          display: "table-cell",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 70,
                        }}
                      >
                        <Tooltip title={row.StoreName} arrow>
                          <span>{row.StoreName}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          pl: 1,
                          display: "table-cell",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 70,
                        }}
                      >
                        <Tooltip title={row.ProjectType} arrow>
                          <span>{row.ProjectType}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          pl: 1,
                          display: "table-cell",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 70,
                        }}
                      >
                        <Tooltip title={row.OrderType} arrow>
                          <span>{row.OrderType}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          pl: 1,
                          width: 70,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Tooltip title={row.ModelStoreNumber} arrow>
                          <span>{row.ModelStoreNumber}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          pl: 1,
                          display: "table-cell",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 70,
                        }}
                      >
                        <Tooltip title={row.AssignedMac} arrow>
                          <span>{row.AssignedMac}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          pl: 1,
                          display: "table-cell",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 70,
                        }}
                      >
                        <Tooltip title={row.AssignedPlanner} arrow>
                          <span>{row.AssignedPlanner}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          pl: 1,
                          display: "table-cell",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 70,
                        }}
                      >
                        <Tooltip
                          title={
                            !!row?.IsArchived ? "Archived" : row?.ProjectStatus
                          }
                          arrow
                        >
                          <span>
                            {!!row?.IsArchived
                              ? "Archived"
                              : row?.ProjectStatus}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          pl: 1,
                          display: "table-cell",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 70,
                        }}
                      >
                        <Tooltip title={row.City} arrow>
                          <span>{row.City}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          pl: 1,
                          display: "table-cell",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 70,
                        }}
                      >
                        <Tooltip title={row.State} arrow>
                          <span>{row.State}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          pl: 1,
                          display: "table-cell",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          width: "120px",
                        }}
                      >
                        <Tooltip title={row.CreatedDate} arrow>
                          <span>{row.CreatedDate}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell padding="none" sx={{ minWidth: 50 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-around",
                          }}
                        >
                          <IconButton
                            aria-label="more"
                            id="long-button"
                            aria-controls={open ? "long-menu" : undefined}
                            aria-expanded={open ? "true" : undefined}
                            aria-haspopup="true"
                            onClick={handleActionMenuClk(row)}
                            sx={{ mr: 2 }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow>
                  <TableCell colSpan={12} />
                </TableRow>
              )}
            </TableBody>
          </Table>
          {props.data.length === 0 && (
            <Typography
              sx={{
                color: "gray",
                width: "100%",
                paddingY: 2,
                fontSize: 14,
                display: "flex",
                justifyContent: "center",
              }}
            >
              No data found.
            </Typography>
          )}
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={rowPerPageOptions}
          component="div"
          count={props.data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
          ActionsComponent={CustomPaginationActions}
        />
      </Paper>
      <Menu
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": "long-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: 48 * 4.5,
            width: "25ch",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {menuRenderer}
      </Menu>
    </Box>
  );
}
